import { useQuery } from "@tanstack/react-query";
import { useHotel } from "@/hooks/useHotel";
import {
  CheckoutFeedback,
  CheckoutFeedbackFilter,
  CommunicationFeedbackFilter,
  RequestFeedbackFilter,
} from "@/services/interfaces/IFeedbackService";
import { feedbacksService } from "@/services/feedbacks-service";

export const feedbackKeys = {
  all: ["feedbacks"] as const,

  requests: () => [...feedbackKeys.all, "requests"] as const,
  requestsList: (filters: RequestFeedbackFilter) =>
    [...feedbackKeys.requests(), filters] as const,

  communications: () => [...feedbackKeys.all, "communications"] as const,
  communicationsList: (filters: CommunicationFeedbackFilter) =>
    [...feedbackKeys.communications(), filters] as const,

  checkouts: () => [...feedbackKeys.all, "checkouts"] as const,
  checkoutsList: (filters: CheckoutFeedbackFilter) =>
    [...feedbackKeys.checkouts(), filters] as const,
  checkoutsSummaryList: (
    bookingIds: string[],
    roomIds: string[],
    hotelId: string,
  ) =>
    [
      ...feedbackKeys.checkouts(),
      "summary",
      { bookingIds, roomIds, hotelId },
    ] as const,
};

export interface CheckoutFeedbackSummary {
  count: number;
  lowestRating: number | null;
}

const CHECKOUT_FEEDBACK_SUMMARY_PAGE_LIMIT = 100;

export const useRequestsFeedbacks = (
  filters?: Omit<RequestFeedbackFilter, "hotelId">,
  enabled = true,
) => {
  const { hotelId } = useHotel();

  const { page = 1, limit = 10, ...restFilters } = filters || {};

  return useQuery({
    queryKey: feedbackKeys.requestsList({
      ...restFilters,
      page,
      limit,
      hotelId: hotelId || "",
    }),
    queryFn: () =>
      feedbacksService.getAllRequestFeedbacks({
        ...filters,
        hotelId: hotelId || "",
      } as RequestFeedbackFilter),
    enabled: enabled && !!hotelId,
  });
};

export const useCommunicationsFeedbacks = (
  filters: Omit<CommunicationFeedbackFilter, "hotelId">,
  enabled = true,
) => {
  const { hotelId } = useHotel();

  const { page = 1, limit = 10, ...restFilters } = filters || {};

  return useQuery({
    queryKey: feedbackKeys.communicationsList({
      ...restFilters,
      page,
      limit,
      hotelId: hotelId || "",
    }),
    queryFn: () =>
      feedbacksService.getAllCommunicationFeedbacks({
        ...filters,
        hotelId: hotelId || "",
      } as CommunicationFeedbackFilter),
    enabled: enabled && !!hotelId,
  });
};

export const useCheckoutsFeedbacks = (
  filters: Omit<CheckoutFeedbackFilter, "hotelId">,
  enabled = true,
) => {
  const { hotelId } = useHotel();

  const { page = 1, limit = 10, ...restFilters } = filters || {};

  return useQuery({
    queryKey: feedbackKeys.checkoutsList({
      ...restFilters,
      page,
      limit,
      hotelId: hotelId || "",
    }),
    queryFn: () =>
      feedbacksService.getAllCheckoutFeedbacks({
        ...filters,
        hotelId: hotelId || "",
      } as CheckoutFeedbackFilter),
    enabled: enabled && !!hotelId,
  });
};

export const useCheckoutFeedbackSummaries = (
  bookingIds: string[],
  roomIds: string[],
  enabled = true,
) => {
  const { hotelId } = useHotel();

  const normalizedBookingIds = [...new Set(bookingIds)].filter(Boolean).sort();
  const normalizedRoomIds = [...new Set(roomIds)].filter(Boolean).sort();

  return useQuery({
    queryKey: feedbackKeys.checkoutsSummaryList(
      normalizedBookingIds,
      normalizedRoomIds,
      hotelId || "",
    ),
    queryFn: async (): Promise<Record<string, CheckoutFeedbackSummary>> => {
      const bookingIdsSet = new Set(normalizedBookingIds);
      const summaries: Record<string, CheckoutFeedbackSummary> = {};

      let currentPage = 1;
      let totalFeedbacks = 0;
      let loadedFeedbacks = 0;

      do {
        const response = await feedbacksService.getAllCheckoutFeedbacks({
          hotelId: hotelId || "",
          page: currentPage,
          limit: CHECKOUT_FEEDBACK_SUMMARY_PAGE_LIMIT,
          roomIds: normalizedRoomIds.length > 0 ? normalizedRoomIds : undefined,
          sortBy: "createdAt",
          sortOrder: "desc",
        });

        totalFeedbacks = response.total;
        loadedFeedbacks += response.data.length;

        response.data.forEach((feedback: CheckoutFeedback) => {
          if (!bookingIdsSet.has(feedback.bookingId)) {
            return;
          }

          const existingSummary = summaries[feedback.bookingId];
          summaries[feedback.bookingId] = {
            count: (existingSummary?.count ?? 0) + 1,
            lowestRating:
              existingSummary?.lowestRating == null
                ? feedback.rating
                : Math.min(existingSummary.lowestRating, feedback.rating),
          };
        });

        if (response.data.length === 0) {
          break;
        }

        currentPage += 1;
      } while (loadedFeedbacks < totalFeedbacks);

      return summaries;
    },
    enabled: enabled && !!hotelId && normalizedBookingIds.length > 0,
  });
};
